import { PDFDocument, StandardFonts } from "pdf-lib"

import { monto } from "@/componentes/tesoreria/dinero"
import { comoFecha, comoFechaHora } from "@/lib/fechas"

import {
  ALTO,
  ANCHO,
  enMonedas,
  escribible,
  GRIS,
  LINEA,
  type Lienzo,
  MARGEN,
  membrete,
  nuevaPagina,
  ROJO,
  texto,
  UTIL,
} from "./pdfReporte"
import type { Saldo } from "./tipos"
import type { CuentaDisponible, Disponibilidad } from "./tiposDisponibilidad"

/**
 * El PDF de la disponibilidad bancaria, compuesto en el navegador (13/10). El mismo
 * papel, tinta y membrete que el reporte de cuentas por pagar (`pdfReporte.ts`): una
 * tabla con cada cuenta, su saldo y su último movimiento, y la nota de las que engañan.
 */

const COLUMNAS = [
  { rotulo: "Cuenta", ancho: 0 },
  { rotulo: "Último movimiento", ancho: 110 },
  { rotulo: "Saldo", ancho: 130, derecha: true },
]
const CUENTA = UTIL - COLUMNAS.reduce((s, c) => s + c.ancho, 0) - 2 * 6
const ANCHOS = COLUMNAS.map((c) => (c.rotulo === "Cuenta" ? CUENTA : c.ancho))

export async function pdfDeLaDisponibilidad(
  d: Disponibilidad,
  logo: Uint8Array | null,
): Promise<Uint8Array<ArrayBuffer>> {
  const pdf = await PDFDocument.create()
  pdf.setTitle(d.titulo)
  const l: Lienzo = {
    pdf,
    pagina: pdf.addPage([ANCHO, ALTO]),
    y: ALTO - MARGEN,
    normal: await pdf.embedFont(StandardFonts.Helvetica),
    negrita: await pdf.embedFont(StandardFonts.HelveticaBold),
  }
  membrete(l, d, logo ? await pdf.embedPng(logo) : null)
  texto(l, d.titulo, MARGEN, l.y, { cuerpo: 16, fuente: l.negrita })
  l.y -= 15
  texto(l, `Generado el ${comoFechaHora(d.generado_en)}`, MARGEN, l.y, { color: GRIS })
  l.y -= 20

  // Cada sistema en su apartado y con su total: no se suman (13/10).
  const conDos = d.sistema2_leido || d.sistema2_error !== null
  bloque(l, conDos ? "Sistema 3.0" : null, d.total, d.cuentas)
  if (conDos) {
    if (d.sistema2_leido) bloque(l, "Sistema 2.0", d.sistema2_total, d.sistema2_cuentas)
    else {
      if (l.y < MARGEN + 50) nuevaPagina(l)
      texto(l, "Sistema 2.0", MARGEN, l.y, { cuerpo: 12, fuente: l.negrita })
      l.y -= 16
      texto(l, `No se pudo leer: ${d.sistema2_error ?? ""}`, MARGEN, l.y, { color: ROJO })
      l.y -= 20
    }
  }

  // Las notas, al PIE y aparte (pedido del usuario, 13/10).
  if (d.notas.length > 0) {
    if (l.y - (24 + d.notas.length * 12) < MARGEN + 14) nuevaPagina(l)
    l.y -= 10
    texto(l, "Notas", MARGEN, l.y, { cuerpo: 9.5, fuente: l.negrita })
    l.y -= 13
    for (const n of d.notas) {
      texto(l, `• ${n}`, MARGEN, l.y, { cuerpo: 8.5, color: GRIS })
      l.y -= 12
    }
  }

  const paginas = pdf.getPages()
  paginas.forEach((p, i) => {
    const pie = escribible(
      l.normal,
      `${d.empresa_nombre} · ${d.titulo} · página ${i + 1} de ${paginas.length}`,
    )
    p.drawText(pie, {
      x: ANCHO - MARGEN - l.normal.widthOfTextAtSize(pie, 7),
      y: MARGEN / 2,
      size: 7,
      font: l.normal,
      color: GRIS,
    })
  })
  return new Uint8Array(await pdf.save())
}

/** El total y la tabla de cuentas de UN sistema, con su rótulo si hay dos. */
function bloque(l: Lienzo, rotulo: string | null, total: Saldo[], cuentas: CuentaDisponible[]) {
  if (l.y < MARGEN + 90) nuevaPagina(l)
  if (rotulo) {
    texto(l, rotulo, MARGEN, l.y, { cuerpo: 12, fuente: l.negrita })
    l.y -= 16
  }
  texto(l, `Total por moneda: ${enMonedas(total)}`, MARGEN, l.y, { cuerpo: 10, fuente: l.negrita })
  l.y -= 22

  let x = MARGEN
  COLUMNAS.forEach((c, i) => {
    texto(l, c.rotulo, x, l.y, {
      cuerpo: 7.5,
      fuente: l.negrita,
      color: GRIS,
      derecha: c.derecha ? ANCHOS[i] : undefined,
    })
    x += (ANCHOS[i] ?? 0) + 6
  })
  l.y -= 5
  l.pagina.drawLine({
    start: { x: MARGEN, y: l.y },
    end: { x: ANCHO - MARGEN, y: l.y },
    thickness: 0.5,
    color: LINEA,
  })
  l.y -= 13

  if (cuentas.length === 0) {
    texto(l, "No hay cuentas activas.", MARGEN, l.y, { color: GRIS })
    l.y -= 16
  }
  for (const c of cuentas) {
    const alto = 16
    if (l.y - alto < MARGEN + 14) nuevaPagina(l)
    texto(l, c.cuenta, MARGEN, l.y, { cuerpo: 9.5, fuente: l.negrita })
    texto(l, comoFecha(c.ultimo_movimiento) || "—", MARGEN + CUENTA + 6, l.y)
    texto(l, `${c.moneda} ${monto(c.saldo)}`, MARGEN + CUENTA + (ANCHOS[1] ?? 0) + 12, l.y, {
      cuerpo: 9.5,
      fuente: l.negrita,
      color: c.saldo.startsWith("-") ? ROJO : undefined,
      derecha: ANCHOS[2],
    })
    l.y -= alto
    l.pagina.drawLine({
      start: { x: MARGEN, y: l.y + 8 },
      end: { x: ANCHO - MARGEN, y: l.y + 8 },
      thickness: 0.4,
      color: LINEA,
    })
  }
  l.y -= 16
}
